import { useHotkey } from '@tanstack/react-hotkeys';
import React from 'react';
import Icon from './Icon';
import './KeyboardShortcuts.css';

const isMac = navigator.platform.toUpperCase().includes('MAC');

interface Shortcut {
  label: string;
  macKeys: string[];
  winKeys: string[];
}

const shortcuts: Shortcut[] = [
  {
    label: 'Toggle Microphone (Mute / Unmute)',
    macKeys: ['⌘', 'D'],
    winKeys: ['Ctrl', 'D'],
  },
  {
    label: 'Toggle Camera',
    macKeys: ['⌘', 'E'],
    winKeys: ['Ctrl', 'E'],
  },
  {
    label: 'Raise / Lower Hand',
    macKeys: ['Ctrl', '⌘', 'H'],
    winKeys: ['Ctrl', 'Alt', 'H'],
  },
  {
    label: 'Show / Hide Participants',
    macKeys: ['Ctrl', '⌘', 'P'],
    winKeys: ['Ctrl', 'Alt', 'P'],
  },
  {
    label: 'Show Keyboard Shortcuts',
    macKeys: ['Shift', '?'],
    winKeys: ['Shift', '?'],
  },
];

interface KeyboardShortcutsProps {
  open: boolean;
  onClose: () => void;
}

const KeyboardShortcuts: React.FC<KeyboardShortcutsProps> = ({
  open,
  onClose,
}) => {
  useHotkey('Escape', () => onClose(), { enabled: open });

  if (!open) return null;

  return (
    <div
      className="keyboard-shortcuts-overlay"
      onClick={onClose}
      role="dialog"
      aria-label="Keyboard shortcuts"
      aria-modal="true">
      <div
        className="keyboard-shortcuts-dialog"
        onClick={e => e.stopPropagation()}>
        <div className="keyboard-shortcuts-header">
          <h2 className="keyboard-shortcuts-title">Keyboard Shortcuts</h2>
          <button
            className="keyboard-shortcuts-close"
            onClick={onClose}
            aria-label="Close">
            <Icon
              name="close"
              size={24}
              fill="var(--cometchat-calls-icon-color-primary)"
            />
          </button>
        </div>
        <div className="keyboard-shortcuts-list">
          {shortcuts.map(shortcut => {
            const keys = isMac ? shortcut.macKeys : shortcut.winKeys;
            return (
              <div className="keyboard-shortcut-row" key={shortcut.label}>
                <span className="shortcut-label">{shortcut.label}</span>
                <div className="shortcut-keys">
                  {keys.map((key, i) => (
                    <React.Fragment key={key + i}>
                      {i > 0 && <span className="shortcut-plus">+</span>}
                      <kbd className="shortcut-key">{key}</kbd>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcuts;
